import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddUserSection } from "@/components/organization/AddUserSection";
import { DeleteUserModal } from "@/components/organization/DeleteUserModal";
import { Enable2FAModal } from "@/components/organization/Enable2FAModal";
import { OrganizationDetailsCard } from "@/components/organization/OrganizationDetailsCard";
import { OrganizationMembersCard } from "@/components/organization/OrganizationMembersCard";
import { OrganizationSecuritySettingsCard } from "@/components/organization/OrganizationSecuritySettingsCard";
import { Card } from "@/components/ui/Card";
import { USER_ROLES } from "@/constants/auth";
import useAuthStore from "@/context/auth/useAuthStore";
import { useOrganizationActions } from "@/context/organization/useOrganizationStore";
import { useEffectiveCustomerId } from "@/hooks/useEffectiveCustomerId";
import { useOrganizationNameEditor } from "@/hooks/useOrganizationNameEditor";
import { useOrganizationUserColumns } from "@/hooks/useOrganizationUserColumns";
import { usePermissions } from "@/hooks/usePermissions";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrganizationSettings } from "@/hooks/useOrganizationSettings";
import { DashboardPageLayout, DashboardPageLoading, DashboardPageSection } from "@/layouts/DashboardPageLayout";
import { API_KEYS_PAGE } from "@/router/paths";
export default function OrganizationPage() {
    const { setCurrentUserRole, reset } = useOrganizationActions();
    const { tokenData } = useAuthStore();
    const { isSuperAdmin, isViewingAllOrgs, isViewingOwnOrg } = useEffectiveCustomerId();
    const { isViewer, isAdmin } = usePermissions();
    const navigate = useNavigate();
    const [deleteUser, setDeleteUser] = useState(null);
    const [lastUserId, setLastUserId] = useState(null);
    const [showEnable2FAModal, setShowEnable2FAModal] = useState(false);
    // Organization settings hook for 2FA enforcement
    const { settings: orgSettings, isLoading: isLoadingSettings, updateSettings, isUpdating: isUpdatingSettings } = useOrganizationSettings();
    const canViewMembers = isViewer || isAdmin || isSuperAdmin;
    const canManageUsers = isViewingOwnOrg; // Can only manage users in own org
    useEffect(()=>{
        if (isViewingAllOrgs) {
            navigate(API_KEYS_PAGE, {
                replace: true
            });
        }
    }, [
        isViewingAllOrgs,
        navigate
    ]);
    const { users, userLimit, organizationName, organizationEmail, isLoading, error, refetch, addUser, deleteUser: deleteUserMutation, updateOrganizationName, isAddingUser, isDeletingUser, isUpdatingName } = useOrganization();
    const organizationNameEditor = useOrganizationNameEditor({
        updateOrganizationName
    });
    useEffect(()=>{
        // Reset store if user has changed
        if (tokenData?.sub && tokenData.sub !== lastUserId) {
            reset();
            setLastUserId(tokenData.sub);
        }
    }, [
        lastUserId,
        reset,
        tokenData?.sub
    ]);
    // Derive current user role from users data (only if we can view members)
    const currentUser = canViewMembers ? users.find((user)=>user.UserId === tokenData?.sub) : null;
    const currentUserRole = currentUser?.Role || tokenData?.role;
    useEffect(()=>{
        // Only update the store's current user role
        setCurrentUserRole(currentUserRole);
    }, [
        currentUserRole,
        setCurrentUserRole
    ]);
    const handleAddUser = async (formData)=>{
        await addUser(formData);
    };
    const handleDeleteUser = async (userId)=>{
        await deleteUserMutation(userId);
        setDeleteUser(null);
    };
    const isCurrentUserAdmin = currentUserRole === USER_ROLES.ADMIN || currentUserRole === USER_ROLES.SUPERADMIN;
    const canModifyUsers = isCurrentUserAdmin && canManageUsers; // Only admins viewing their own org can modify users
    const columns = useOrganizationUserColumns({
        canModifyUsers,
        currentUserId: tokenData?.sub ?? undefined,
        isDeletingUser,
        onRequestDelete: setDeleteUser
    });
    // Organization data
    // When viewing own org, use tokenData email; when viewing other org, use organizationEmail from API
    const displayEmail = isViewingOwnOrg ? tokenData?.email || "" : organizationEmail || "";
    const organizationData = {
        name: organizationName || tokenData?.customerIdentifier || "Organization",
        email: displayEmail,
        members: users.length,
        userLimit: userLimit
    };
    // Show main loading state on initial load (only if can view members)
    if (canViewMembers && isLoading && users.length === 0) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "Organization",
            description: "Manage your organization settings and team members",
            children: /*#__PURE__*/ _jsx(DashboardPageLoading, {})
        });
    }
    return /*#__PURE__*/ _jsxs(DashboardPageLayout, {
        title: "Organization",
        description: "Manage your organization settings and team members",
        actions: canModifyUsers ? /*#__PURE__*/ _jsx(AddUserSection, {
            onAddUser: handleAddUser,
            isLoading: isLoading || isAddingUser
        }) : null,
        children: [
            /*#__PURE__*/ _jsxs(DashboardPageSection, {
                className: "grid grid-cols-1 gap-6 lg:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsx(OrganizationDetailsCard, {
                        organizationName: organizationData.name,
                        organizationEmail: organizationData.email,
                        membersCount: organizationData.members,
                        canViewMembers: canViewMembers,
                        canModifyUsers: canModifyUsers,
                        currentUserRole: currentUserRole,
                        isViewingOwnOrg: isViewingOwnOrg,
                        isEditingName: organizationNameEditor.isEditingName,
                        editedName: organizationNameEditor.editedName,
                        nameSaveError: organizationNameEditor.nameSaveError,
                        isUpdatingName: isUpdatingName,
                        onEditedNameChange: organizationNameEditor.setEditedName,
                        onStartEditName: ()=>{
                            organizationNameEditor.startEditing(organizationName || tokenData?.customerIdentifier || "");
                        },
                        onCancelEditName: organizationNameEditor.cancelEditing,
                        onSaveName: ()=>{
                            void organizationNameEditor.saveEditedName();
                        }
                    }),
                    canModifyUsers && /*#__PURE__*/ _jsx(Card, {
                        title: "Usage & Limits",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "space-y-4",
                            children: /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mb-2 flex justify-between text-sm",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-gray-600",
                                                children: "Team Members"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "font-medium text-gray-900",
                                                children: [
                                                    organizationData.members,
                                                    " / ",
                                                    organizationData.userLimit
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-2 w-full rounded-full bg-gray-200",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: "h-2 rounded-full bg-blue-600",
                                            style: {
                                                width: `${Math.min(organizationData.members / organizationData.userLimit * 100, 100)}%`
                                            }
                                        })
                                    })
                                ]
                            })
                        })
                    })
                ]
            }),
            canModifyUsers && /*#__PURE__*/ _jsx(DashboardPageSection, {
                children: /*#__PURE__*/ _jsx(OrganizationSecuritySettingsCard, {
                    isEnforce2FAEnabled: orgSettings.enforce2FA,
                    isLoadingSettings: isLoadingSettings,
                    isUpdatingSettings: isUpdatingSettings,
                    onOpenEnable2FAModal: ()=>setShowEnable2FAModal(true)
                })
            }),
            canViewMembers && /*#__PURE__*/ _jsx(OrganizationMembersCard, {
                users: users,
                isLoading: isLoading,
                error: error,
                columns: columns,
                onRetry: refetch
            }),
            deleteUser && /*#__PURE__*/ _jsx(DeleteUserModal, {
                isOpen: !!deleteUser,
                user: deleteUser,
                onClose: ()=>setDeleteUser(null),
                onConfirm: ()=>handleDeleteUser(deleteUser.UserId),
                isLoading: isDeletingUser
            }),
            showEnable2FAModal && /*#__PURE__*/ _jsx(Enable2FAModal, {
                onClose: ()=>setShowEnable2FAModal(false),
                onConfirm: async ()=>{
                    await updateSettings({
                        enforce2FA: true
                    });
                },
                isLoading: isUpdatingSettings
            })
        ]
    });
}
